import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import type { SweepCheckedOffReason } from '../../../types/sweep';
import { useTheme } from '../../../contexts/ThemeContext';

export interface SweepCheckOffDialogProps {
  open: boolean;
  onClose: () => void;
  locationLabel: string;
  onConfirm: (reason: SweepCheckedOffReason, notes: string) => void | Promise<void>;
}

const SweepCheckOffDialog: React.FC<SweepCheckOffDialogProps> = ({
  open,
  onClose,
  locationLabel,
  onConfirm,
}) => {
  const { colors } = useTheme();
  const [reason, setReason] = useState<SweepCheckedOffReason>('completed');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setReason('completed');
    setNotes('');
    setError('');
    setBusy(false);
  }, [open, locationLabel]);

  const submit = async () => {
    if (reason === 'other' && !notes.trim()) {
      setError('Please enter notes when you select Other.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await onConfirm(reason, notes.trim());
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: colors.TextPrimary, fontWeight: 700 }}>Remove from list</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="body2" color={colors.TextSecondary}>
          <strong>{locationLabel}</strong> will be hidden from Report Site → Browse sweep locations (regardless of
          visit date) until you restore it under Sweeps.
        </Typography>
        <RadioGroup
          value={reason}
          onChange={(e) => setReason(e.target.value as SweepCheckedOffReason)}
        >
          <FormControlLabel
            value="completed"
            control={<Radio sx={{ color: '#22d3ee' }} />}
            label={<Typography color={colors.TextPrimary}>Completed</Typography>}
          />
          <FormControlLabel
            value="store_closed"
            control={<Radio sx={{ color: '#22d3ee' }} />}
            label={<Typography color={colors.TextPrimary}>Store closed</Typography>}
          />
          <FormControlLabel
            value="other"
            control={<Radio sx={{ color: '#22d3ee' }} />}
            label={<Typography color={colors.TextPrimary}>Other</Typography>}
          />
        </RadioGroup>
        <TextField
          label={reason === 'other' ? 'Notes (required)' : 'Notes (optional)'}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          error={!!error}
          helperText={error}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '12px' },
            '& input, & textarea': { color: colors.TextPrimary },
            '& label': { color: colors.TextMuted },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: colors.TextMuted }}>
          Cancel
        </Button>
        <Button onClick={() => void submit()} disabled={busy} variant="outlined" sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}>
          Check off
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SweepCheckOffDialog;
